/**
 * User Overview Component
 * Displays user profile information, statistics, and quick actions
 * Follows the Help Center UX pattern with quick action cards and stat widgets
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Package, CreditCard, User, HelpCircle, CheckCircle, Phone } from 'lucide-react';
import { auth } from '../../services/firebase';
import { QuickActionsGrid, QuickAction } from '../common/QuickActionsGrid';
import { StatsWidget, StatItem } from '../common/StatsWidget';

interface UserOverviewProps {
  profile: any;
  statistics: any;
  syncing: boolean;
}

export default function UserOverview({ profile, statistics, syncing }: UserOverviewProps): JSX.Element {
  const user = auth.currentUser;
  const navigate = useNavigate();

  // Quick actions for user dashboard
  const quickActions: QuickAction[] = useMemo(() => [
    {
      id: 'orders',
      title: 'My Orders',
      description: 'View order history and track purchases',
      icon: 'shopping-bag',
      linkUrl: '/user?tab=orders',
      buttonText: 'View Orders',
      variant: 'primary',
    },
    {
      id: 'products',
      title: 'My Products',
      description: 'Access purchased products and downloads',
      icon: 'package',
      linkUrl: '/user?tab=products',
      buttonText: 'View Products',
      variant: 'success',
    },
    {
      id: 'help',
      title: 'Get Help',
      description: 'Browse help articles and FAQs',
      icon: 'help-circle',
      linkUrl: '/help',
      buttonText: 'Help Center',
      variant: 'default',
    },
  ], []);

  // Statistics for stat widgets
  const stats: StatItem[] = useMemo(() => {
    if (!statistics) return [];

    return [
      {
        id: 'orders',
        label: 'Total Orders',
        value: statistics.totalOrders || 0,
        icon: ShoppingBag,
        variant: 'primary',
        linkUrl: '/user?tab=orders',
        linkText: 'View all',
      },
      {
        id: 'products',
        label: 'Products Owned',
        value: statistics.totalProducts || 0,
        icon: Package,
        variant: 'success',
        linkUrl: '/user?tab=products',
        linkText: 'View all',
      },
      {
        id: 'spent',
        label: 'Total Spent',
        value: `$${(statistics.totalSpent || 0).toFixed(2)}`,
        icon: CreditCard,
        variant: 'default',
      },
    ];
  }, [statistics]);

  return (
    <div className="space-y-8">
      {/* Syncing Indicator */}
      {syncing && (
        <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <span className="animate-spin mr-2">⟳</span>
          Syncing your data...
        </div>
      )}

      {/* Quick Actions Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h2>
        <QuickActionsGrid actions={quickActions} columns={3} />
      </section>

      {/* Statistics Section */}
      {statistics && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Your Statistics
          </h2>
          <StatsWidget stats={stats} columns={3} />
        </section>
      )}

      {/* Account Information Card */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Account Information
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <User size={32} className="text-primary-600 dark:text-primary-400" />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Display Name
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {profile?.displayName || user?.displayName || 'Not set'}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Member Since
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {profile?.createdAt
                      ? new Date(profile.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'Unknown'}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Email Address
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {profile?.email || user?.email}
                    </p>
                    {user?.emailVerified ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        <CheckCircle size={12} className="mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                        Unverified
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Phone Number
                  </p>
                  <div className="flex items-center gap-2">
                    {user?.phoneNumber ? (
                      <>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {user.phoneNumber}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          <CheckCircle size={12} className="mr-1" />
                          Verified
                        </span>
                      </>
                    ) : (
                      <button
                        onClick={() => navigate('/verify-phone')}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        <Phone size={14} />
                        Add phone number
                      </button>
                    )}
                  </div>
                </div>



                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    User ID
                  </p>
                  <p className="text-sm font-mono text-gray-600 dark:text-gray-400 truncate">
                    {profile?.uid || user?.uid}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help CTA */}
      <section className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Need assistance?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Check out our help center for guides and FAQs
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/help')}
            className="btn btn-primary whitespace-nowrap"
          >
            Visit Help Center
          </button>
        </div>
      </section>
    </div>
  );
}
